"use client";

import { Comedian, getPublishedComedians } from "@/data/comedians";
import {
  PostalCodeData,
  UNKNOWN_POSTAL_CODE,
  getIpData,
  lookupZipcodeData,
} from "@/data/zipcode";
import { ShowsNearbyEvents, trackEvent } from "@/analytics";
import type { GeoPoint } from "@punchup/data/models";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import { Database } from "@punchup/data/supabase.types";
import PageMargin from "@/components/common/PageMargin";
import { ShowWithComedian, getPrimaryShowComedian } from "@/data/shows";
import ShowsNearbyContent from "./ShowsNearbyContent";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import { genAbsoluteURL } from "../common/utils";
import { isTixologiWidgetLink } from "@punchup/tixologi/widget-origin";
import { logException, logMessage } from "@/analytics/sentry";
import { updateTixologiAvailabilityClient } from "@/utils/tixologi-availability";
import { useAutoJoinWaitlist } from "@/hooks/useAutoJoinWaitlist";
import { useGate } from "statsig-react";
import useSession from "@/hooks/useSession";
import { useTicketmasterNearbyShows } from "@/hooks/useTicketmasterShows";

type UpdateLocationFormValues = {
  city: string;
  zipcode: string;
  countryCode: string;
  radius: number;
};

const DEFAULT_RADIUS_MILES = 25;

// Helper functions for getting current time - easier for tests to mock
const getCurrentTimeISO = () => dayjs().toISOString();
const getCurrentTimeUTC = () => dayjs().utc().toISOString();

// Configurable parameters for Ticketmaster supplemental shows
const SUPPLEMENTAL_CONFIG = {
  // Minimum number of unique comedians required to skip Ticketmaster data
  MIN_COMEDIAN_THRESHOLD: 25,
  // Time period in days to look ahead for calculations
  TIME_PERIOD_DAYS: 90,
  // Override to always supplement shows with Ticketmaster data regardless of comedian count
  OVERRIDE: true,
  // Maximum number of shows per week to fetch from Ticketmaster, just to avoid over pollution
  MAX_SHOWS_PER_WEEK: 10,
} as const;

type SupplementalLocation = {
  city: string | null;
  stateCode?: string | null;
  countryCode: string | null;
  point?: GeoPoint;
};

function pointOf({
  latitude,
  longitude,
}: PostalCodeData): GeoPoint | undefined {
  return latitude !== null && longitude !== null
    ? { latitude, longitude }
    : undefined;
}

interface ShowsNearbyPageProps {
  followedComedianIds: string[];
}

export default function ShowsNearbyPage({
  followedComedianIds,
}: ShowsNearbyPageProps) {
  const [location, setLocation] = useState<PostalCodeData | null>(null);
  const [shows, setShows] = useState<ShowWithComedian[] | null>(null);
  const [supplementalShows, setSupplementalShows] = useState<
    ShowWithComedian[]
  >([]);
  const [platformComedians, setPlatformComedians] = useState<Comedian[]>([]);
  const [shouldFetchSupplemental, setShouldFetchSupplemental] = useState(false);
  const [supplementalParams, setSupplementalParams] = useState<{
    city: string;
    stateCode?: string;
    countryCode: string;
    point?: GeoPoint;
    radius: number;
    startDateTime: string;
    endDateTime: string;
    maxShowsPerWeek: number;
  } | null>(null);
  const [invalidZipcode, setInvalidZipcode] = useState(false);
  const [showNoLocationError, setShowNoLocationError] = useState(false);
  const [detectedCountryCode, setDetectedCountryCode] = useState<string | null>(
    null
  );
  const [buyTicketsShowId, setBuyTicketsShowId] = useState<string>("");

  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Auto-join waitlist after authentication redirect
  useAutoJoinWaitlist();

  const zipCode = searchParams.get("postalCode");
  const city = searchParams.get("city");
  const countryCode = searchParams.get("countryCode");
  const radius = searchParams.get("radius")
    ? parseInt(searchParams.get("radius")!)
    : DEFAULT_RADIUS_MILES;

  const [formValues, setFormValues] = useState<UpdateLocationFormValues>({
    // initialize to the values in the URL
    city: city ?? "",
    zipcode: zipCode ?? "",
    countryCode: countryCode ?? "",
    radius,
  });

  const session = useSession();
  const supabase = createClientComponentClient<Database>();
  const router = useRouter();

  // Statsig gate for supplemental shows feature
  const { value: supplementalShowsGateEnabled } = useGate(
    "supplement_shows_nearby_with_tm_shows"
  );

  // Internal experiment: a gated switch that asks /api/shows for shows with an
  // empty lineup, which the page otherwise never fetches nor renders.
  const { value: zeroLineupToggleEnabled } = useGate(
    "shows_nearby_zero_lineup_toggle"
  );
  const [includeZeroLineup, setIncludeZeroLineup] = useState(false);
  const shouldIncludeZeroLineup = zeroLineupToggleEnabled && includeZeroLineup;

  // Use React Query to fetch and cache Ticketmaster shows
  const {
    data: ticketmasterShows = [],
    isLoading: isLoadingTicketmaster,
    error: ticketmasterError,
  } = useTicketmasterNearbyShows({
    shouldFetch:
      shouldFetchSupplemental &&
      !!supplementalParams &&
      !!supplementalParams.countryCode,
    city: supplementalParams?.city || "",
    stateCode: supplementalParams?.stateCode,
    countryCode: supplementalParams?.countryCode || "US",
    point: supplementalParams?.point,
    radius: supplementalParams?.radius || 25,
    startDateTime: supplementalParams?.startDateTime || getCurrentTimeISO(),
    endDateTime:
      supplementalParams?.endDateTime ||
      dayjs().add(SUPPLEMENTAL_CONFIG.TIME_PERIOD_DAYS, "days").toISOString(),
    maxShowsPerWeek:
      supplementalParams?.maxShowsPerWeek ||
      SUPPLEMENTAL_CONFIG.MAX_SHOWS_PER_WEEK,
  });

  const isUS = useMemo(() => {
    if (formValues.countryCode !== "") {
      return formValues.countryCode === "US";
    }
    return detectedCountryCode === "US";
  }, [formValues.countryCode, detectedCountryCode]);

  // Get a new searchParams string by merging the current searchParams
  // with a set of new ones, provided in an object of key/value pairs
  // modified based on Next's "Updating searchParams"
  // https://nextjs.org/docs/app/api-reference/functions/use-search-params#updating-searchparams
  const createQueryString = useCallback(
    (newParamsMap: { [key: string]: string }, paramsToRemove?: string[]) => {
      // copy the existing search params
      const params = new URLSearchParams(searchParams.toString());

      // remove specified parameters
      if (paramsToRemove) {
        paramsToRemove.forEach((paramName) => {
          params.delete(paramName);
        });
      }

      // get the names of all new params specified in the object
      const newParamNames = Object.keys(newParamsMap);

      newParamNames.forEach((name: string) => {
        // set all new param names & values
        const value = newParamsMap[name];
        params.set(name, value);
      });

      return params.toString();
    },
    [searchParams]
  );

  // navigate to a the current URL with updated query params
  // based the passed object of key/value pairs
  // [optional] replace: if true, replace the current history entry instead of pushing a new one
  const updateQueryParams = useCallback(
    (
      newParamsMap: { [key: string]: string },
      replace: boolean = false,
      paramsToRemove?: string[]
    ) => {
      if (replace) {
        router.replace(
          pathname + "?" + createQueryString(newParamsMap, paramsToRemove)
        );
      } else {
        router.push(
          pathname + "?" + createQueryString(newParamsMap, paramsToRemove)
        );
      }
    },
    [pathname, createQueryString, router]
  );

  useEffect(() => {
    trackEvent(ShowsNearbyEvents.LOADED_SHOWS_NEARBY_PAGE);
  }, []);

  // Separate useEffect to load platform comedians
  useEffect(() => {
    const loadComedians = async () => {
      try {
        const comedians = await getPublishedComedians();
        setPlatformComedians(comedians);
      } catch (error) {
        console.error("Error fetching platform comedians:", error);
      }
    };

    loadComedians();
  }, []); // Empty dependency array - only run once on mount

  // Fetch zip code from IP address if not logged in or no zipcode available
  const fetchZipAndCountryFallback = useCallback(async () => {
    const { postalCode: zipcodeFromIP, countryCode: countryCodeFromIP } =
      await getIpData();

    // initialize the form values with the detected values
    if (zipcodeFromIP && zipcodeFromIP !== UNKNOWN_POSTAL_CODE) {
      setFormValues((prev) => ({ ...prev, zipcode: zipcodeFromIP }));
    }
    if (countryCodeFromIP) {
      setDetectedCountryCode(countryCodeFromIP);
    }

    if (
      zipcodeFromIP &&
      zipcodeFromIP !== UNKNOWN_POSTAL_CODE &&
      countryCodeFromIP
    ) {
      // For now, we'll continue using postal code for IP detection
      // In the future, this could be updated to use city lookup from IP
      const newFields = {
        postalCode: zipcodeFromIP,
        countryCode: countryCodeFromIP,
      };
      setFormValues((prev) => ({ ...prev, ...newFields }));
      updateQueryParams(newFields);
    } else {
      trackEvent(ShowsNearbyEvents.NO_LOCATION_FOUND, {
        IpZipcode: zipcodeFromIP,
        IpCountryCode: countryCodeFromIP,
      });
      logMessage(ShowsNearbyEvents.NO_LOCATION_FOUND, "warning");
      setShowNoLocationError(true);
    }
  }, [updateQueryParams]);

  useEffect(() => {
    // if not logged in -AND- one of zip or country is not set
    if (session === null && (!zipCode || !countryCode)) {
      fetchZipAndCountryFallback();
    }

    // if logged in, but no user -OR- location parameters are already set from URL
    if (
      !session?.user ||
      !!(zipCode && countryCode) ||
      !!(city && countryCode)
    ) {
      return;
    }

    // Get the user's zipcode & country code from the database
    supabase
      .from("user_zipcodes")
      .select("zipcode,country_code")
      .eq("user", session.user.id)
      .eq("from_ip", false)
      .maybeSingle()
      .then(({ data: existingRow }) => {
        if (existingRow) {
          const newFields = {
            // add zipcode if present
            ...(existingRow.zipcode ? { postalCode: existingRow.zipcode } : {}),
            // add countryCode if present
            ...(existingRow.country_code
              ? { countryCode: existingRow.country_code }
              : {}),
          };
          setFormValues((prev) => ({ ...prev, ...newFields }));
          updateQueryParams(newFields, true);
        } else {
          fetchZipAndCountryFallback();
        }
      });
  }, [
    session,
    supabase,
    updateQueryParams,
    zipCode,
    countryCode,
    city,
    fetchZipAndCountryFallback,
  ]);

  // Helper function to match Ticketmaster comedian names with platform comedians
  const matchComedianWithPlatform = useCallback(
    (tmComedianName: string): Comedian | null => {
      if (!tmComedianName || platformComedians.length === 0) return null;

      // Normalize name for matching (remove extra spaces, convert to lowercase)
      const normalizeName = (name: string) =>
        name.trim().toLowerCase().replace(/\s+/g, " ");

      const normalizedTmName = normalizeName(tmComedianName);

      // Try exact match first
      let match = platformComedians.find(
        (comedian) => normalizeName(comedian.display_name) === normalizedTmName
      );

      if (match) {
        return match;
      }

      // Try partial matches (for cases like "John Smith" vs "John Smith Jr.")
      match = platformComedians.find((comedian) => {
        const normalizedPlatformName = normalizeName(comedian.display_name);
        return (
          normalizedPlatformName.includes(normalizedTmName) ||
          normalizedTmName.includes(normalizedPlatformName)
        );
      });

      if (match) {
        return match;
      }

      return null;
    },
    [platformComedians]
  );

  // Helper function to enhance Ticketmaster shows with platform comedian data
  const enhanceTicketmasterShows = useCallback(
    (tmShows: ShowWithComedian[]): ShowWithComedian[] => {
      return tmShows.map((tmShow) => {
        // Get the comedian name from the show
        const tmComedianName = getPrimaryShowComedian(tmShow)?.display_name;

        if (!tmComedianName) return tmShow;

        const platformComedian = matchComedianWithPlatform(tmComedianName);

        if (platformComedian) {
          // Replace the placeholder comedian data with real platform data
          const enhancedShow = {
            ...tmShow,
            comedian_id: platformComedian.id,
            comedian: {
              ...platformComedian,
              is_punchup_verified: true, // Platform comedians are verified
            },
            show_comedians: [
              {
                ...platformComedian,
                is_punchup_verified: true,
                ordering: 1,
                hidden_from_comedian_page: false,
              },
            ],
          };

          return enhancedShow;
        }

        return tmShow;
      });
    },
    [matchComedianWithPlatform]
  );

  // Helper function to check if we need supplemental shows and trigger fetch
  const checkAndSetupSupplementalShows = useCallback(
    (
      platformShows: ShowWithComedian[],
      { city: cityName, stateCode, countryCode, point }: SupplementalLocation
    ) => {
      try {
        // Filter shows in the configured time period
        const now = dayjs();
        const endDate = now.add(SUPPLEMENTAL_CONFIG.TIME_PERIOD_DAYS, "days");
        const showsInTimePeriod = platformShows.filter((show) => {
          const showDate = dayjs(show.datetime);
          return showDate.isAfter(now) && showDate.isBefore(endDate);
        });

        // Get unique comedians from shows in the time period
        const uniqueComedianIds = new Set<string>();
        showsInTimePeriod.forEach((show) => {
          // Add show comedians if they exist
          if (show.show_comedians) {
            show.show_comedians.forEach((comedian) => {
              uniqueComedianIds.add(comedian.id);
            });
          }
        });

        const uniqueComedianCount = uniqueComedianIds.size;

        // Setup supplemental shows fetch if override is true OR we have fewer than the minimum comedian threshold (AND the gate is enabled)
        // IMPORTANT: countryCode is now required for Ticketmaster API
        if (
          (SUPPLEMENTAL_CONFIG.OVERRIDE ||
            uniqueComedianCount < SUPPLEMENTAL_CONFIG.MIN_COMEDIAN_THRESHOLD) &&
          cityName &&
          countryCode &&
          supplementalShowsGateEnabled
        ) {
          // Set parameters for React Query to fetch
          setSupplementalParams({
            city: cityName,
            stateCode: stateCode || undefined,
            countryCode: countryCode,
            point,
            radius: radius,
            startDateTime: getCurrentTimeISO(),
            endDateTime: dayjs()
              .add(SUPPLEMENTAL_CONFIG.TIME_PERIOD_DAYS, "days")
              .toISOString(),
            maxShowsPerWeek: SUPPLEMENTAL_CONFIG.MAX_SHOWS_PER_WEEK,
          });
          setShouldFetchSupplemental(true);
        } else {
          const skipReason = !supplementalShowsGateEnabled
            ? "gate disabled"
            : !countryCode
              ? "missing required country code"
              : SUPPLEMENTAL_CONFIG.OVERRIDE
                ? "override enabled but missing location data"
                : `${uniqueComedianCount} >= ${SUPPLEMENTAL_CONFIG.MIN_COMEDIAN_THRESHOLD} comedians`;
          setShouldFetchSupplemental(false);
          setSupplementalParams(null);
          setSupplementalShows([]);
        }
      } catch (error) {
        console.error("Error setting up supplemental shows:", error);
        setShouldFetchSupplemental(false);
        setSupplementalParams(null);
        setSupplementalShows([]);
      }
    },
    [radius, supplementalShowsGateEnabled]
  );

  const fetchShows = useCallback(async () => {
    // Reset states
    setShows(null);
    setLocation(null);
    setSupplementalShows([]);
    setShouldFetchSupplemental(false);
    setSupplementalParams(null);
    setInvalidZipcode(false);

    // /api/shows only returns lineup-less shows when asked, and this page
    // renders every show under a comedian row — so outside the experiment,
    // anything without a primary comedian is dropped before any count,
    // grouping, or render sees it.
    const zeroLineupParam: Record<string, string> = shouldIncludeZeroLineup
      ? { includeZeroLineup: "true" }
      : {};
    const isRenderableNearbyShow = (show: ShowWithComedian) =>
      shouldIncludeZeroLineup || getPrimaryShowComedian(show) !== null;

    // Handle city search
    if (city && countryCode) {
      // City search might include state code in format "City, ST"
      const cityParts = city.split(",").map((part) => part.trim());
      const cityName = cityParts[0];

      // If there's a state code or state name in the city string (e.g., "Chicago, IL" or "London, England")
      const stateCode = cityParts.length > 1 ? cityParts[1] : null;

      // When override is enabled, trigger TM fetch immediately (parallel with internal API)
      if (
        SUPPLEMENTAL_CONFIG.OVERRIDE &&
        supplementalShowsGateEnabled &&
        cityName &&
        countryCode
      ) {
        setSupplementalParams({
          city: cityName,
          stateCode: stateCode || undefined,
          countryCode: countryCode,
          radius: radius,
          startDateTime: getCurrentTimeISO(),
          endDateTime: dayjs()
            .add(SUPPLEMENTAL_CONFIG.TIME_PERIOD_DAYS, "days")
            .toISOString(),
          maxShowsPerWeek: SUPPLEMENTAL_CONFIG.MAX_SHOWS_PER_WEEK,
        });
        setShouldFetchSupplemental(true);
      }

      try {
        // The shows API already handles city search, just pass the parameters directly
        const params = new URLSearchParams({
          city: cityName,
          countryCode,
          radius: radius.toString(),
          ...(stateCode && { stateCode }),
          startDatetime: getCurrentTimeUTC(),
          ...zeroLineupParam,
        });

        const response = await fetch(
          genAbsoluteURL(`/api/shows?${params.toString()}`)
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const nearbyShows = (
          (await response.json()) as ShowWithComedian[]
        ).filter(isRenderableNearbyShow);

        // Track the event
        trackEvent(ShowsNearbyEvents.RESULTS_RETURNED, {
          City: cityName,
          StateCode: stateCode || "",
          CountryCode: countryCode,
          Radius: radius,
          ShowsReturned: nearbyShows.length,
          Location: city,
        });

        const processedShows = nearbyShows;
        const showsWithAvailability =
          await updateTixologiAvailabilityClient(processedShows);
        setShows(showsWithAvailability);

        // Check if we need supplemental shows (fewer than configured comedian threshold in time period)
        if (countryCode) {
          checkAndSetupSupplementalShows(nearbyShows, {
            city: cityName,
            stateCode,
            countryCode,
          });
        }

        // Set location for display purposes (always set for city search)
        const stateAbbr = stateCode || "";
        setLocation({
          postal_code: "",
          city: cityName,
          state: stateAbbr,
          state_abbreviation: stateAbbr,
          country_code: countryCode,
          latitude: null,
          longitude: null,
        });
      } catch (error) {
        logException(error, { extra: { city, countryCode } });
        setInvalidZipcode(true);
      }
    }
    // Handle postal code search
    else if (zipCode && countryCode) {
      try {
        // Always get location data for postal code to ensure display is accurate
        const zipcodeData = await lookupZipcodeData(zipCode, countryCode);
        if (zipcodeData) {
          setLocation(zipcodeData);

          // When override is enabled, trigger TM fetch immediately (parallel with internal API)
          if (
            SUPPLEMENTAL_CONFIG.OVERRIDE &&
            supplementalShowsGateEnabled &&
            zipcodeData.city &&
            countryCode
          ) {
            setSupplementalParams({
              city: zipcodeData.city,
              stateCode: zipcodeData.state_abbreviation || undefined,
              countryCode: countryCode,
              point: pointOf(zipcodeData),
              radius: radius,
              startDateTime: getCurrentTimeISO(),
              endDateTime: dayjs()
                .add(SUPPLEMENTAL_CONFIG.TIME_PERIOD_DAYS, "days")
                .toISOString(),
              maxShowsPerWeek: SUPPLEMENTAL_CONFIG.MAX_SHOWS_PER_WEEK,
            });
            setShouldFetchSupplemental(true);
          }
        } else {
          trackEvent(ShowsNearbyEvents.UNKNOWN_ZIPCODE_FOUND, {
            Zipcode: zipCode,
            CountryCode: countryCode,
          });
          setInvalidZipcode(true);
          return;
        }

        // The shows API already handles postal code search
        const params = new URLSearchParams({
          postalCode: zipCode,
          countryCode,
          radius: radius.toString(),
          startDatetime: getCurrentTimeUTC(),
          ...zeroLineupParam,
        });

        const response = await fetch(
          genAbsoluteURL(`/api/shows?${params.toString()}`)
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const nearbyShows = (
          (await response.json()) as ShowWithComedian[]
        ).filter(isRenderableNearbyShow);

        // Track the event
        trackEvent(ShowsNearbyEvents.RESULTS_RETURNED, {
          Zipcode: zipCode,
          CountryCode: countryCode,
          Radius: radius,
          ShowsReturned: nearbyShows.length,
          Location: zipcodeData
            ? `${zipcodeData.city}, ${zipcodeData.state_abbreviation}`
            : zipCode,
        });

        const processedShows = nearbyShows;

        // Check if we need supplemental shows (fewer than configured comedian threshold in time period) for postal code search
        const showsWithAvailability =
          await updateTixologiAvailabilityClient(processedShows);
        setShows(showsWithAvailability);
        if (zipcodeData && countryCode) {
          checkAndSetupSupplementalShows(nearbyShows, {
            city: zipcodeData.city,
            stateCode: zipcodeData.state_abbreviation,
            countryCode,
            point: pointOf(zipcodeData),
          });
        }
      } catch (error) {
        logException(error, { extra: { zipCode, countryCode } });
        setInvalidZipcode(true);
      }
    } else {
      // No search parameters provided
      setShowNoLocationError(true);
    }
  }, [
    city,
    zipCode,
    countryCode,
    radius,
    checkAndSetupSupplementalShows,
    supplementalShowsGateEnabled,
    shouldIncludeZeroLineup,
  ]);

  // Process Ticketmaster shows from React Query
  useEffect(() => {
    if (!shouldFetchSupplemental || !shows) return;

    if (isLoadingTicketmaster) {
      return;
    }

    if (ticketmasterError) {
      console.error("❌ React Query Ticketmaster error:", ticketmasterError);
      setSupplementalShows([]);
      return;
    }

    if (Array.isArray(ticketmasterShows) && ticketmasterShows.length > 0) {
      // Dedupe Ticketmaster shows - remove only those with same date AND comedian name as platform shows
      const platformShowKeys = new Set(
        shows.map((show) => {
          const date = dayjs(show.datetime).format("YYYY-MM-DD");
          const comedianName =
            getPrimaryShowComedian(show)?.display_name?.toLowerCase().trim() ||
            "";
          return `${date}:${comedianName}`;
        })
      );

      const dedupedTicketmasterShows = ticketmasterShows.filter(
        (tmShow: ShowWithComedian) => {
          const tmDate = dayjs(tmShow.datetime).format("YYYY-MM-DD");
          const tmComedianName =
            getPrimaryShowComedian(tmShow)
              ?.display_name?.toLowerCase()
              .trim() || "";
          const tmKey = `${tmDate}:${tmComedianName}`;
          const isFiltered = platformShowKeys.has(tmKey);

          return !isFiltered;
        }
      );

      // Enhance deduplicated shows with platform comedian data
      const enhancedShows = enhanceTicketmasterShows(dedupedTicketmasterShows);

      setSupplementalShows(enhancedShows);
    } else {
      setSupplementalShows([]);
    }
  }, [
    shouldFetchSupplemental,
    shows,
    ticketmasterShows,
    isLoadingTicketmaster,
    ticketmasterError,
    enhanceTicketmasterShows,
  ]);

  // Add this useEffect hook to call fetchShows when relevant params change
  // Add it after the fetchShows function definition

  useEffect(() => {
    // Skip if we don't have either zipCode+countryCode or city+countryCode
    if ((!zipCode || !countryCode) && (!city || !countryCode)) {
      // If there are no location parameters at all, show the no location error
      if (!zipCode && !city) {
        setShowNoLocationError(true);
      }
      return;
    }

    // Reset error states
    setInvalidZipcode(false);
    setShowNoLocationError(false);

    // Fetch shows based on location params
    fetchShows();
  }, [city, zipCode, countryCode, radius, fetchShows]);

  const fastCheckoutShows =
    shows?.filter((show) => isTixologiWidgetLink(show.ticket_link)) || [];

  return (
    <ShowsNearbyContent
      shows={shows}
      fastCheckoutShows={fastCheckoutShows}
      supplementalShows={supplementalShows}
      showNoLocationError={showNoLocationError}
      invalidZipcode={invalidZipcode}
      isUS={isUS}
      zipCode={zipCode}
      city={city}
      location={location}
      buyTicketsShowId={buyTicketsShowId}
      setBuyTicketsShowId={setBuyTicketsShowId}
      followedComedianIds={followedComedianIds}
      countryCode={countryCode}
      radius={radius}
      detectedCountryCode={detectedCountryCode}
      updateQueryParams={updateQueryParams}
      pathname={pathname}
      zeroLineupExperiment={
        zeroLineupToggleEnabled
          ? {
              includeZeroLineup,
              onToggle: () => setIncludeZeroLineup((value) => !value),
            }
          : undefined
      }
    />
  );
}
